import {
  type LineItem,
  type PlainLineItem,
  type ResolvedGroupedCartEntryKIT,
  type SubLineItem,
} from "../../../../../models/CartData";
import { type CSSProperties, useEffect, useRef } from "react";
import classNames from "classnames";
import { ManualPackageRestrictionContext } from "../../../../../../../../common/components/PackageRestrictionContext/PackageRestrictionContext";
import CartEntryProductInfo from "../../../CartEntryProductInfo";
import Spinner from "../../../../../../../../common/components/Spinner/Spinner";
import PackageEntryList from "../../PackageEntryList";
import { useSelector } from "react-redux";
import { selectIsCartAboveThreshold } from "../../../../../redux/selectors/cartSelectors";
import { CopyLastRowLink, LoadPnaLink, SubtotalsRow } from "../PlainProductEntry";
import { IncludedPartsSection } from "./IncludedPartsSection";
import { type RootState } from "../../../../../../../../redux/store";
import { OptionalPartsSection } from "./OptionalPartsSection";
import * as PriceUtils from "../../../../../../../../utils/PriceUtils";
import { multiply, volatileSum, zero } from "../../../../../../../../utils/PriceUtils";
import { lineItemHasSurcharge } from "../../../../../redux/selectors/productEntrySelectors";
import { IncludedSurcharge } from "../../../../../../../../common/components/Product/ProductParams";
import { isUniversalKITProduct } from "../../../../../../../../models/ProductData";
import {
  getSurchargeValueFromTotalPrice,
  getTotalPriceForLineItem,
} from "../../../../../../../ProductDescriptor/v1/productDescriptorUtils";
import { useTranslation } from "react-i18next";
import { useCartPageContext } from "~/feature/cart/cart/CartPageContext";
import { useIsBundledQuote } from "~/feature/cart/cart/IsBundledQuoteContext";
import { isProductEntryFcc } from "~/feature/cart/cart/components/CartPage/ProductTable/ProductEntryList/productEntryHelper";
import { Alert } from "~/common/m3/Alert/Alert";

interface ProductEntryProps {
  groupingEntry: ResolvedGroupedCartEntryKIT;
  className?: string;
  editMode?: boolean;
  index: number;
  style?: CSSProperties;
  onRender?: (element: Element) => void;
}

export const KITProductLineItem = (props: ProductEntryProps) => {
  const {
    groupingEntry: { cartEntry, packEntry, productDescriptor },
    className,
    editMode,
    style,
    onRender,
    index,
  } = props;
  const { t } = useTranslation();

  const { showSubtotal } = useCartPageContext();

  const isInCheckout = !editMode;
  const isAboveThreshold = useSelector(selectIsCartAboveThreshold);

  const subLineItems = useSelector(selectSubLineItems(packEntry as PlainLineItem));

  const isPnaLoading = productDescriptor.isLoading;
  const isPnaError = productDescriptor.isError;
  let isPnaLoaded: boolean;
  if (isInCheckout) {
    isPnaLoaded = !!productDescriptor.availability;
  } else {
    isPnaLoaded = productDescriptor.isLoaded;
  }

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onRender && rootRef.current) {
      window.setTimeout(() => {
        if (onRender && rootRef.current) {
          onRender(rootRef.current);
        }
      }, 16);
    }
  }, [onRender, cartEntry, packEntry, productDescriptor]);

  const cls = classNames(className, "scha__cart__product-entry productentry_" + props.index);

  const isEntireEntryFromQuote = !!packEntry.quoteNumber;
  const canOnlyLoadAvailability = useIsBundledQuote() || isEntireEntryFromQuote;

  const showPleaseWait = isPnaLoading && editMode && isAboveThreshold;

  let showLoadPnaLink = false;
  if (editMode) {
    if (canOnlyLoadAvailability) {
      showLoadPnaLink = !productDescriptor.availability;
    } else {
      showLoadPnaLink = !productDescriptor.isLoaded;
    }
  }

  const entryQuantity =
    packEntry.quantity +
    subLineItems.reduce((sum, i) => sum + i.quantity, 0) +
    productDescriptor.base.includedParts.reduce((sum, i) => sum + i.quantity * packEntry.quantity, 0);

  const deliveryAmount = (productDescriptor.base.includedParts.length > 0 ? 1 : 0) + subLineItems.length;

  const subTotalItems: LineItem[] = [...subLineItems];
  if (isUniversalKITProduct(productDescriptor)) {
    subTotalItems.push(packEntry);
  }

  const subtotal = volatileSum(...subTotalItems.map((item) => multiply(item.netPrice ?? zero(), item.quantity)));

  const allLineItems = [packEntry, ...subLineItems];
  const showSurcharge = allLineItems.some(lineItemHasSurcharge) && subtotal.value > 0;

  const surchargeTotal = PriceUtils.volatileSum(
    ...allLineItems.filter(lineItemHasSurcharge).map((lineItem) => calculateSurcharge(lineItem, false)),
  );

  const isFcc = isProductEntryFcc(props.groupingEntry);

  return (
    <div className={cls} style={style} data-test-id={`product-entry`} ref={rootRef}>
      <div className="scha__cart__product-table__left-side">
        <ManualPackageRestrictionContext restrictedPackNo={packEntry.restrictedPackage ? packEntry.packCode : ""}>
          <CartEntryProductInfo productDescriptor={productDescriptor} isFcc={isFcc} />
        </ManualPackageRestrictionContext>
      </div>

      <div className="scha__cart__product-table__right-side">
        {showPleaseWait && <Spinner className={"my-2"} />}

        <PackageEntryList editMode={editMode} packEntryIds={[packEntry.id]} />

        {productDescriptor.base.includedParts.length > 0 && (
          <IncludedPartsSection kitQty={packEntry.quantity} descriptor={productDescriptor} editMode={editMode} />
        )}

        {subLineItems.length > 0 && <OptionalPartsSection subLineItems={subLineItems} editMode={editMode} />}

        <div className="mt-2 mb-3">
          {(editMode || !isPnaLoaded) && (
            <div className={"scha__cart__product-table__right-side__summary-container"}>
              {editMode && productDescriptor.base.quantityVisible && (
                <div className={"pl-3"}>
                  <CopyLastRowLink entryId={packEntry.id} />
                </div>
              )}
              {showLoadPnaLink && (
                <div className={"pl-2"}>
                  <LoadPnaLink
                    productCode={cartEntry.productCode}
                    disabled={isPnaLoading}
                    isEditMode={editMode}
                    loadAvailabilityOnly={canOnlyLoadAvailability}
                  />
                </div>
              )}
              {!showLoadPnaLink && <div />}

              {showSurcharge && <IncludedSurcharge className={"pl-2"} value={surchargeTotal} />}
            </div>
          )}
        </div>

        {!isPnaLoading && isPnaError && !window.SCHA.config.disableAllWS && (
          <div className="px-3 my-3">
            <Alert severity={"error"} className={"my-2"}>
              {t("page.cart.product.pnaLoadingError")}
            </Alert>
          </div>
        )}
        {showSubtotal && (
          <SubtotalsRow
            product={productDescriptor.base}
            totalQty={entryQuantity}
            deliveryAmount={deliveryAmount}
            subtotal={subtotal}
            hasSurcharge={showSurcharge}
          />
        )}
      </div>
    </div>
  );
};

function selectSubLineItems(packEntry: PlainLineItem) {
  return (state: RootState) => {
    return packEntry.optionalPartIds.map((id) => state.cart.packEntriesById[id]) as SubLineItem[];
  };
}

export const calculateSurcharge = (lineItem: LineItem, isSubscription: boolean) => {
  if (!lineItemHasSurcharge(lineItem)) {
    return PriceUtils.zero();
  }
  const totalPrice = getTotalPriceForLineItem(lineItem, isSubscription);
  return getSurchargeValueFromTotalPrice(totalPrice);
};
